/**
 * Warming a screen before it is opened. The live runtime registers how (`app/screen-runtime.tsx`: the screen's chunk
 * and the validators its commands wait for); the navigation asks for it on idle after login and when a rail item is
 * pressed or focused. It loads code only: nothing is read, and Identity still decides whether the screen opens. In the
 * proof gallery and the specs nothing is registered (a spec may register its own) and asking does nothing.
 */
/** `idle`: the screen's code only. `intent` (pressed or focused): also what its first command would wait for. */
export type ScreenPrefetchReason = 'idle' | 'intent';
let prefetch: ((path: string, reason: ScreenPrefetchReason) => void) | null = null;

export function registerScreenPrefetch(load: ((path: string, reason: ScreenPrefetchReason) => void) | null): void {
  prefetch = load;
}

export function prefetchScreen(path: string, reason: ScreenPrefetchReason = 'intent'): void {
  prefetch?.(path, reason);
}

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: Readonly<{ timeout: number }>) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Warms the screens behind these paths one at a time, each when the browser is idle, so nothing competes with what
 * the person is doing. Returns the cancel.
 */
export function prefetchScreensWhenIdle(paths: readonly string[]): () => void {
  const idle = window as IdleWindow;
  const queue = [...new Set(paths)];
  let handle: number | null = null;
  let cancelled = false;
  const schedule = () => {
    if (cancelled || queue.length === 0 || prefetch === null) return;
    const next = () => { handle = null; prefetchScreen(queue.shift()!, 'idle'); schedule(); };
    handle = idle.requestIdleCallback ? idle.requestIdleCallback(next, { timeout: 4_000 }) : window.setTimeout(next, 400);
  };
  schedule();
  return () => {
    cancelled = true;
    if (handle === null) return;
    if (idle.cancelIdleCallback) idle.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}
